import { AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { GenerateRunSummaryRequest } from "@/types/requests";
import { GetAdhocRunPersonasRequest } from "@/types/requests/adhoc/runs";
import {
  DemographicFiltersMap,
  GroupResult,
  Persona,
  Summary,
} from "@/types/resources";
import {
  CreateAdhocRunResponse,
  GetAdhocRunPersonasResponse,
  GetAdhocRunResultsResponse,
} from "@/types/responses";

const serializeFilters = (
  filters: DemographicFiltersMap
): Record<string, string> => {
  const serialized: Record<string, string> = {};

  Object.entries(filters).forEach(([key, values]) => {
    if (!values || values.length === 0) return;
    serialized[key] = values.join(",");
  });

  return serialized;
};

const denormalizeGroups = (
  wireGroups: GetAdhocRunResultsResponse["results"],
  personas: Record<string, Persona>
): GroupResult[] =>
  wireGroups.map((g) => ({
    ...g,
    answers: g.answers.map(({ persona_id, ...rest }) => ({
      ...rest,
      persona: personas[persona_id],
    })),
  }));

const useTriggerRun = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const client = useAxiosClient(`/adhoc/questions/${questionId}/runs`);

  const triggerRun = useCallback(async () => {
    setError(null);
    setRunId(null);
    setLoading(true);

    try {
      const response = await client.post<
        CreateAdhocRunResponse,
        AxiosResponse<CreateAdhocRunResponse>,
        Record<string, never>
      >("", {});
      setRunId(response.data.run_id);
      return response.data.run_id;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    error,
    loading,
    runId,
    triggerRun,
  };
};

const useGetRunResults = (
  questionId: string,
  runId: string | null,
  filters?: DemographicFiltersMap
) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<GroupResult[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);

  const client = useAxiosClient(`/adhoc/questions/${questionId}/runs`);

  // Recompute only when the *content* of `filters` changes (not its identity),
  // since `serializedFilters` feeds downstream callbacks/effects. Keying the
  // memo on the JSON string keeps the dependency a single simple expression, as
  // the hook lint rules require.
  const filtersKey = filters ? JSON.stringify(filters) : undefined;
  const serializedFilters = useMemo(
    () =>
      filtersKey
        ? serializeFilters(JSON.parse(filtersKey) as DemographicFiltersMap)
        : undefined,
    [filtersKey]
  );

  const fetchResults = useCallback(() => {
    if (!runId) {
      return;
    }

    client
      .get<GetAdhocRunResultsResponse>(`/${runId}`, {
        params: serializedFilters ?? {},
      })
      .then((response) => {
        setResults(
          denormalizeGroups(response.data.results, response.data.personas)
        );
        setSummary(response.data.summary);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, runId, serializedFilters]);

  // Reset the list during render (not in an effect) when the run or filters
  // change, and re-enter the loading state (mirroring the original fetch, which
  // only set loading past its `!runId` early return). Mount needs no reset.
  const inputsKey = JSON.stringify([runId, serializedFilters]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setResults([]);
    setSummary(null);
    if (runId) {
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const refresh = useCallback(() => {
    if (runId) {
      setLoading(true);
      setError(null);
    }
    fetchResults();
  }, [fetchResults, runId]);

  return {
    error,
    loading,
    refresh,
    results,
    summary,
  };
};

const useGetRunPersonas = (
  questionId: string,
  runId: string,
  filters?: DemographicFiltersMap
) => {
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [personas, setPersonas] = useState<Persona[]>([]);

  const client = useAxiosClient(`/adhoc/questions/${questionId}/runs`);

  const serializedFilters = useMemo(
    () => (filters ? serializeFilters(filters) : undefined),
    [filters]
  );

  const getPersonas = useCallback(
    (params: GetAdhocRunPersonasRequest) => {
      if (!questionId || !runId) {
        return;
      }

      client
        .get<GetAdhocRunPersonasResponse>(`/${runId}/personas`, { params })
        .then((response) => {
          setCount(response.data.count);
          setPersonas(response.data.personas);
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client, runId, questionId]
  );

  // Re-enter the loading state during render when the inputs change, mirroring
  // the original fetch (which only set loading past its early return).
  const queryKey = JSON.stringify([questionId, runId, serializedFilters]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    if (questionId && runId) {
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    getPersonas(serializedFilters ?? {});
  }, [serializedFilters, getPersonas]);

  const refresh = useCallback(() => {
    if (questionId && runId) {
      setLoading(true);
      setError(null);
    }
    getPersonas(serializedFilters ?? {});
  }, [getPersonas, questionId, runId, serializedFilters]);

  return {
    count,
    error,
    loading,
    personas,
    refresh,
  };
};

const useGenerateRunSummary = (questionId: string, runId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);

  const client = useAxiosClient(`/adhoc/questions/${questionId}/runs`);

  const generateSummary = useCallback(
    async (body: GenerateRunSummaryRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          Summary,
          AxiosResponse<Summary>,
          GenerateRunSummaryRequest
        >(`/${runId}/summary`, body);
        setSummary(response.data);
        return response.data;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, runId]
  );

  return {
    error,
    generateSummary,
    loading,
    summary,
  };
};

export {
  useGenerateRunSummary,
  useGetRunPersonas,
  useGetRunResults,
  useTriggerRun,
};
